import { useEffect, useState, useRef } from 'react';
import {
  useApi,
  microsoftAuthApiRef,
  SessionState,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * MicrosoftLoginPicker - A scaffolder field extension that requires Microsoft authentication
 */
export const MicrosoftLoginPicker = (
  props: FieldExtensionComponentProps<boolean>,
) => {
  const { onChange } = props;
  const microsoftAuthApi = useApi(microsoftAuthApiRef);

  const [isSignedIn, setIsSignedIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasAttemptedSignIn = useRef(false);

  // 1. Observe whether user is signed in
  useEffect(() => {
    const subscription = microsoftAuthApi.sessionState$().subscribe(state => {
      const signedIn = state === SessionState.SignedIn;
      setIsSignedIn(signedIn);
      onChange(signedIn);
    });
    return () => subscription.unsubscribe();
  }, [microsoftAuthApi, onChange]);

  // 2. If not signed in, trigger sign-in popup (only once)
  useEffect(() => {
    if (!isSignedIn && !hasAttemptedSignIn.current) {
      hasAttemptedSignIn.current = true;
      microsoftAuthApi
        .signIn()
        .catch(e => setError(`Azure Entra login failed: ${e.message}`));
    }
  }, [isSignedIn, microsoftAuthApi]);

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}{' '}
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            setError(null);
            microsoftAuthApi
              .signIn()
              .catch(e => setError(`Azure Entra login failed: ${e.message}`));
          }}
          sx={{ verticalAlign: 'baseline' }}
        >
          Retry login
        </Link>
      </Typography>
    );
  }

  if (!isSignedIn) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="body2" color="text.secondary">
          Signing in to Azure Entra...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
      <Typography variant="body2" color="text.secondary">
        Authenticated via Azure Entra
      </Typography>
    </Box>
  );
};
